import React, { useMemo, useState, useRef } from 'react'
import { Controller, Control } from 'react-hook-form'
import { InputLabel, Popper } from '@mui/material'
import * as Icons from '@mui/icons-material'

type IconDef = {
  name: string
  label: string
}

type Props = {
  control: Control<any>
  name: string
  icons: IconDef[]
  label?: string
  disabled?: boolean
}

const IconPickerInput: React.FC<Props> = ({ control, name, label, disabled, icons }) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const filteredIcons = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return icons
    return icons.filter(it => it.label.toLowerCase().includes(q))
  }, [query, icons])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const SelectedIcon = Icons[field.value] ?? Icons[icons[0].name]

        return (
          <div className="relative">
            {label && <InputLabel shrink>{label}</InputLabel>}

            <div className="flex items-center gap-3">
              <button
                ref={buttonRef}
                type="button"
                disabled={disabled}
                onClick={() => setOpen(v => !v)}
                className={[
                  'p-2 rounded border border-gray-200 bg-white shadow-sm',
                  'hover:shadow focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1',
                  disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                ].join(' ')}
                aria-label="Choisir une icône"
                title="Choisir une icône"
              >
                <SelectedIcon fontSize="small" />
              </button>

              <span className="text-sm text-gray-700 font-mono select-all">
                {filteredIcons.find(icon => icon.name === field.value)?.label || 'Aucune'}
              </span>

              <button
                type="button"
                onClick={() => field.onChange(icons[0].name)}
                className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                Reset
              </button>
            </div>

            <Popper
              open={open}
              anchorEl={buttonRef.current}
              placement="top-start"
              style={{ zIndex: 1500 }}
              modifiers={[
                { name: 'offset', options: { offset: [0, 8] } }
              ]}
            >
              <div className="w-72 rounded-md border border-gray-200 bg-white shadow-lg">
                <div className="p-2 border-b border-gray-100">
                  <input
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Rechercher une icône..."
                    className="w-full text-sm px-2 py-1 border rounded"
                    autoFocus
                  />
                </div>

                <div className="max-h-64 overflow-auto p-2 grid grid-cols-6 gap-2">
                  {filteredIcons.map(icon => {
                    const IconComponent = Icons[icon.name]
                    const isSelected = field.value === icon.name
                    return (
                      <button
                        key={icon.name}
                        type="button"
                        onClick={() => { field.onChange(icon.name); setOpen(false) }}
                          className={`
                            flex items-center justify-center p-2 rounded border
                            ${isSelected
                              ? 'border-indigo-500 ring-1 ring-indigo-500'
                              : 'border-transparent hover:border-gray-200'}
                          `}
                        title={icon.name}
                        aria-label={icon.name}
                      >
                        <IconComponent fontSize="small" />
                      </button>
                    )
                  })}
                  {filteredIcons.length === 0 && (
                    <div className="col-span-6 text-center text-xs text-gray-500 py-4">
                      Aucune icône
                    </div>
                  )}
                </div>
              </div>
            </Popper>

            {fieldState.error && (
              <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
            )}
          </div>
        )
      }}
    />
  )
}

export default IconPickerInput
